import type { ReactElement, RefObject } from "react";
import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

export interface SidebarMinimizedFlyoutPortalProps {
  anchorRef: RefObject<HTMLElement | null>;
  label: string;
  open: boolean;
}

/**
 * Minimized sidebar labels render in `document.body` with `position: fixed` so they are not
 * clipped when any ancestor forms a scrollport (`overflow-y: auto` forces computed `overflow-x`
 * to `auto` in browsers — absolute flyouts beside the rail were invisible).
 */
export const SidebarMinimizedFlyoutPortal = ({
  anchorRef,
  label,
  open,
}: SidebarMinimizedFlyoutPortalProps): ReactElement | null => {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    const el = anchorRef.current;
    if (!el) {
      setPos(null);
      return;
    }

    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) {
        setPos(null);
        return;
      }
      const ar = anchor.getBoundingClientRect();
      if (ar.width === 0) {
        setPos(null);
        return;
      }
      const rail = anchor.closest(
        ".sidebar.sidebar-modernized",
      ) as HTMLElement | null;
      const rr = rail?.getBoundingClientRect();
      // Legacy CSS expands `.nav-item:hover` to ~170px so `anchor.right` lands in the main
      // content; pin the flyout to the rail instead.
      setPos({
        left: (rr?.right ?? ar.right) + 8,
        top: ar.top + ar.height / 2,
      });
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);

  if (!open || typeof document === "undefined" || pos === null) {
    return null;
  }

  const leftPx = pos.left;
  const topPx = pos.top;

  // `createPortal` typings can pull a nested `@types/react` (via `@types/react-dom`), which
  // disagrees with this project's React types; assert to the local ReactElement.
  return createPortal(
    <span
      className="sidebar-minimized-flyout-label sidebar-minimized-flyout-label--portal"
      aria-hidden="true"
      style={{
        position: "fixed",
        left: `${leftPx}px`,
        top: `${topPx}px`,
        transform: "translateY(-50%)",
        zIndex: 10006,
      }}
    >
      {label}
    </span>,
    document.body,
  ) as ReactElement;
};
